import * as THREE from 'three';
import { FikoObje3D } from '../../../FikoObje3D';
import { FikoSahne } from '../../../FikoSahne';
import { FikoVektor3D } from '../../../FikoVektor3d';
import { FikoVektorTakimYol3D } from '../../../FikoThreeCam/FikoVektorTakimYol3d';
import { FikoTakimFrezeleme } from '../../../FikoThreeCam/FikoTakimFrezeleme';

/**
 * FikoAktiviteDelik - 2D CAM Delik İşleme Aktivitesi
 *
 * Profesyonel CAM sistemleri (Mastercam, Fusion 360, SolidCAM) standartlarında
 * eksiksiz delik işleme motoru.
 *
 * Desteklenen Delik Tipleri:
 * - Punta (G81): Center/Spot drilling - merkez delme, puntalama
 * - Delme (G83): Peck drilling - kademeli delme, derin delik
 * - Rayba (G85): Reaming - raybalama, hassas delik genişletme
 * - Kilavuz (G84): Tapping - diş çekme, kılavuz çekme
 * - Havsa (G82): Counterboring/Countersinking - havşa açma
 *
 * G-Code Döngüleri:
 * - G81: Simple drilling cycle (Basit delme)
 * - G82: Dwell drilling cycle (Beklemeli delme - Havşa)
 * - G83: Peck drilling cycle (Kademeli delme)
 * - G84: Tapping cycle (Kılavuz döngüsü)
 * - G85: Boring/Reaming cycle (Rayba döngüsü)
 * - G73: High-speed peck drilling (Yüksek hız kademeli)
 * - G86: Boring with spindle stop
 * - G89: Boring with dwell
 *
 * @author FikoYT
 * @version 1.0.0 - Professional Drilling Engine
 */
export class FikoAktiviteDelik extends FikoObje3D {
    static SAYAC = 1;

    override type = 'FikoAktiviteDelik';
    readonly isGroup: true;

    // ═══════════════════════════════════════════════════════════════════
    // DELİK TİPİ
    // ═══════════════════════════════════════════════════════════════════

    delikTipi: 'Punta' | 'Delme' | 'Rayba' | 'Kilavuz' | 'Havsa' = 'Delme';

    // ═══════════════════════════════════════════════════════════════════
    // DELİK NOKTALARI
    // ═══════════════════════════════════════════════════════════════════

    delikNoktalar: FikoVektor3D[] = [];          // Delik merkez noktaları
    delikCaplar: number[] = [];                   // Her delik için çap (mm)
    delikDerinlikler: number[] = [];              // Her delik için derinlik (mm)

    // ═══════════════════════════════════════════════════════════════════
    // DÜZLEM TANIMLARI
    // ═══════════════════════════════════════════════════════════════════

    ustZ: number = 0;                             // Parça üst yüzeyi (R plane)
    altZ: number = -10;                           // Hedef derinlik
    guvenliZ: number = 5;                         // Güvenli yükseklik (G00 Z)
    retraktZ: number = 2;                         // Retrakt düzlemi (R)

    // ═══════════════════════════════════════════════════════════════════
    // TAKIM
    // ═══════════════════════════════════════════════════════════════════

    takim: FikoTakimFrezeleme;
    takimYonu: FikoVektor3D = new FikoVektor3D(0, 0, 1);

    // ═══════════════════════════════════════════════════════════════════
    // PUNTA (SPOT DRILLING) PARAMETRELERİ - G81
    // ═══════════════════════════════════════════════════════════════════

    puntaDerinlik: number = 3;                    // Punta derinliği (mm)
    puntaAci: number = 90;                        // Punta açısı (derece) - 60, 90, 118, 120

    // ═══════════════════════════════════════════════════════════════════
    // DELME (PECK DRILLING) PARAMETRELERİ - G83
    // ═══════════════════════════════════════════════════════════════════

    peckDerinlik: number = 5;                     // Her peck adımı (mm)
    peckRetraktMiktari: number = 0.5;             // Geri çekilme (mm) - talaş temizleme
    peckBekleme: number = 0;                      // Her peck sonrası bekleme (saniye)
    peckTipi: 'Standart' | 'YuksekHiz' | 'TamRetrakt' = 'Standart';
    // Standart: G83 - Her peck'te R'ye retrakt
    // YuksekHiz: G73 - Küçük retrakt (chip break)
    // TamRetrakt: Full retract to initial point

    ilkPeckDerinlik: number = 0;                  // İlk peck derinliği (0 = otomatik)
    peckAzaltmaOrani: number = 0;                 // Her peck'te azaltma (%)
    minPeckDerinlik: number = 1;                  // Minimum peck derinliği (mm)

    // ═══════════════════════════════════════════════════════════════════
    // RAYBA (REAMING) PARAMETRELERİ - G85
    // ═══════════════════════════════════════════════════════════════════

    raybaStok: number = 0.2;                      // Rayba stoku (mm)
    raybaDwellBottom: number = 0.5;               // Alt bekleme (saniye)
    raybaCikisHizi: number = 500;                 // Çıkış hızı (mm/dk) - G85 feed retract

    // ═══════════════════════════════════════════════════════════════════
    // KILAVUZ (TAPPING) PARAMETRELERİ - G84
    // ═══════════════════════════════════════════════════════════════════

    disCap: number = 6;                           // Diş çapı (M6, M8, M10...)
    disAdimi: number = 1;                         // Diş adımı (mm) - pitch
    disTipi: 'Metrik' | 'Whitworth' | 'UNC' | 'UNF' = 'Metrik';
    disDerinlik: number = 10;                     // Diş derinliği (mm)
    disYonu: 'Sag' | 'Sol' = 'Sag';               // Sağ/Sol diş
    rigidTapping: boolean = false;                 // Rigid tapping (senkronize)
    floatingHolder: boolean = true;               // Floating holder kullan
    kilavuzBoyOrani: number = 2;                  // Kılavuz boy oranı (xD)

    // ═══════════════════════════════════════════════════════════════════
    // HAVŞA (COUNTERBORE/COUNTERSINK) PARAMETRELERİ - G82
    // ═══════════════════════════════════════════════════════════════════

    havsaTipi: 'Countersink' | 'Counterbore' | 'SpotFace' = 'Counterbore';
    havsaCap: number = 10;                        // Havşa çapı (mm)
    havsaDerinlik: number = 3;                    // Havşa derinliği (mm)
    havsaAci: number = 90;                        // Havşa açısı (derece) - 82, 90, 100, 120
    havsaDwell: number = 0.5;                     // Dipte bekleme (saniye)

    // ═══════════════════════════════════════════════════════════════════
    // HIZ PARAMETRELERİ
    // ═══════════════════════════════════════════════════════════════════

    ilerlemeDelme: number = 200;                  // Delme ilerleme (mm/dk)
    ilerlemeRetrakt: number = 1000;               // Retrakt hızı (mm/dk)
    hizRapid: number = 10000;                     // Rapid hız (mm/dk)
    devir: number = 3000;                         // Spindle devri (rpm)

    // Tip bazlı özel hızlar
    puntaIlerleme: number = 300;                  // Punta ilerleme
    raybaIlerleme: number = 150;                  // Rayba ilerleme
    kilavuzDevir: number = 500;                   // Kılavuz devri (düşük)

    // ═══════════════════════════════════════════════════════════════════
    // DELİK SIRASI / OPTİMİZASYON
    // ═══════════════════════════════════════════════════════════════════

    siralama: 'Secimsirasi' | 'EnKisaYol' | 'XSonraY' | 'YSonraX' | 'Spiral' = 'EnKisaYol';
    enKisaYolAlgoritma: 'Nearest' | 'TSP' | 'Cluster' = 'Nearest';

    // ═══════════════════════════════════════════════════════════════════
    // SPOT DRILLING (ÖN PUNTALAMA)
    // ═══════════════════════════════════════════════════════════════════

    onPuntaEtkin: boolean = false;                // Delme öncesi punta
    onPuntaDerinlik: number = 2;                  // Ön punta derinliği

    // ═══════════════════════════════════════════════════════════════════
    // ÇİP KIRILMASI / DWELL
    // ═══════════════════════════════════════════════════════════════════

    dipBekleme: number = 0;                       // Dip bekleme (saniye) - G82
    chipBreakEtkin: boolean = true;               // Talaş kırma aktif

    // ═══════════════════════════════════════════════════════════════════
    // GÜVENLİK PARAMETRELERİ
    // ═══════════════════════════════════════════════════════════════════

    delikDerinlikKontrol: boolean = true;         // Derinlik kontrolü
    maxDerinlikOrani: number = 10;                // Max derinlik/çap oranı
    kirikMatkap: boolean = false;                 // Kırık matkap modu (hassas)

    // ═══════════════════════════════════════════════════════════════════
    // SOGUTMA
    // ═══════════════════════════════════════════════════════════════════

    sogutmaTipi: 'Yok' | 'Dis' | 'Ic' | 'Mist' = 'Dis';
    sogutmaBasinc: 'Dusuk' | 'Orta' | 'Yuksek' = 'Orta';

    // ═══════════════════════════════════════════════════════════════════
    // HESAPLANAN VERİLER
    // ═══════════════════════════════════════════════════════════════════

    takimYoluNoktalar: FikoVektorTakimYol3D[] = [];
    hesaplananSure: number = 0;                   // Tahmini işleme süresi (dk)

    // ═══════════════════════════════════════════════════════════════════
    // 3D GÖRÜNTÜLEME
    // ═══════════════════════════════════════════════════════════════════

    takimYoluMesh: THREE.Group;
    delikMarkerlar: THREE.Group;

    // ═══════════════════════════════════════════════════════════════════
    // SAHNE & DİALOG
    // ═══════════════════════════════════════════════════════════════════

    sahne?: FikoSahne;
    private _dialogAciliyor = false;
    private _acikDialog: any;

    constructor(sahne?: FikoSahne) {
        super(sahne);
        this.sahne = sahne;
        this.name = `Delik-${FikoAktiviteDelik.SAYAC++}`;
        this.takim = new FikoTakimFrezeleme(sahne);
        this.takim.takimTipi = 'Matkap';
        this.takim.cap = 6;
        this.takimYoluMesh = new THREE.Group();
        this.delikMarkerlar = new THREE.Group();
        this.add(this.takimYoluMesh);
        this.add(this.delikMarkerlar);
    }

    // ═══════════════════════════════════════════════════════════════════
    // DİALOG YÖNETİMİ
    // ═══════════════════════════════════════════════════════════════════

    /**
     * Düzenle dialogunu aç
     */
    async duzenle($event?: any): Promise<void> {
        if (this._dialogAciliyor) {
            return;
        }

        if (!this.sahne?.anaGosterim?.DialogFiko) {
            console.warn('[FikoAktiviteDelik] DialogFiko servisi bulunamadı');
            return;
        }

        const dialogId = 'fiko-delik-edit-' + this.uuid;
        const dialogService = this.sahne.anaGosterim.DialogFiko;

        if (this._acikDialog && this._acikDialog.miAcik) {
            const dialogEl = document.getElementById(this._acikDialog.id_dialog);
            if (dialogEl) {
                dialogEl.style.setProperty('z-index', '99999', 'important');
                dialogEl.parentElement?.style.setProperty('z-index', '99999', 'important');
            }
            return;
        }

        const mevcutDialog = dialogService.idIleAl?.(dialogId);
        if (mevcutDialog && mevcutDialog.miAcik) {
            this._acikDialog = mevcutDialog;
            return;
        }

        this._acikDialog = undefined;
        this._dialogAciliyor = true;

        const { DialogFikoDelikComponent } = await import('./dialog-fiko-delik');

        const dialog = dialogService.ac({
            bilesen: DialogFikoDelikComponent,
            id: dialogId,
            $event: $event,
            ayarlar: {
                baslik: 'Delik Düzenle: ' + this.name,
                simge: 'target',
                modalMi: false,
                sinirlandi: false,
                boyut: { w: 420, h: 'auto' }
            },
            parametreler: {
                anaGosterim: this.sahne.anaGosterim,
                sahne: this.sahne,
                aktivite: this
            }
        });

        this._acikDialog = dialog;
        this._dialogAciliyor = false;

        if (dialog?.onDialogClose) {
            dialog.onDialogClose.subscribe(() => {
                this._acikDialog = undefined;
            });
        }
    }

    /**
     * Çift tıklandığında düzenle dialogunu aç
     */
    ciftTiklandi(): void {
        this.duzenle();
    }

    // ═══════════════════════════════════════════════════════════════════
    // DELİK NOKTASI EKLEME
    // ═══════════════════════════════════════════════════════════════════

    /**
     * Tek delik noktası ekle
     */
    delikNoktasiEkle(nokta: FikoVektor3D, cap?: number, derinlik?: number): void {
        this.delikNoktalar.push(nokta.clone());
        this.delikCaplar.push(cap ?? this.takim.cap);
        this.delikDerinlikler.push(derinlik ?? Math.abs(this.altZ - this.ustZ));
        this.delikMarkerOlustur(nokta);
    }

    /**
     * Çoklu delik noktası ekle
     */
    delikNoktalariEkle(noktalar: FikoVektor3D[]): void {
        for (const nokta of noktalar) {
            this.delikNoktasiEkle(nokta);
        }
    }

    /**
     * Dairesel desen ile delik oluştur (Bolt Circle)
     */
    daireselDesenOlustur(
        merkez: FikoVektor3D,
        yaricap: number,
        delikSayisi: number,
        baslangicAci: number = 0
    ): void {
        const aciAdim = (Math.PI * 2) / delikSayisi;
        for (let i = 0; i < delikSayisi; i++) {
            const aci = baslangicAci + i * aciAdim;
            const x = merkez.x + yaricap * Math.cos(aci);
            const y = merkez.y + yaricap * Math.sin(aci);
            this.delikNoktasiEkle(new FikoVektor3D(x, y, merkez.z));
        }
    }

    /**
     * Grid desen ile delik oluştur
     */
    gridDesenOlustur(
        baslangic: FikoVektor3D,
        xSayisi: number,
        ySayisi: number,
        xAralik: number,
        yAralik: number
    ): void {
        for (let j = 0; j < ySayisi; j++) {
            for (let i = 0; i < xSayisi; i++) {
                const x = baslangic.x + i * xAralik;
                const y = baslangic.y + j * yAralik;
                this.delikNoktasiEkle(new FikoVektor3D(x, y, baslangic.z));
            }
        }
    }

    // ═══════════════════════════════════════════════════════════════════
    // DELİK SIRALAMA / OPTİMİZASYON
    // ═══════════════════════════════════════════════════════════════════

    /**
     * Delikleri optimize et (en kısa yol)
     */
    delikleriOptimizeEt(): void {
        if (this.delikNoktalar.length < 2) return;

        switch (this.siralama) {
            case 'EnKisaYol':
                this.nearestNeighborSirala();
                break;
            case 'XSonraY':
                this.xySirala(true);
                break;
            case 'YSonraX':
                this.xySirala(false);
                break;
            case 'Spiral':
                this.spiralSirala();
                break;
            // 'Secimsirasi' - değişiklik yok
        }
    }

    /**
     * Nearest Neighbor algoritması
     */
    private nearestNeighborSirala(): void {
        const sirali: number[] = [0];
        const ziyaretEdilmemis = new Set<number>();
        for (let i = 1; i < this.delikNoktalar.length; i++) {
            ziyaretEdilmemis.add(i);
        }

        let mevcutIdx = 0;
        while (ziyaretEdilmemis.size > 0) {
            let enYakinIdx = -1;
            let enYakinMesafe = Infinity;

            for (const idx of ziyaretEdilmemis) {
                const mesafe = this.noktaMesafe(
                    this.delikNoktalar[mevcutIdx],
                    this.delikNoktalar[idx]
                );
                if (mesafe < enYakinMesafe) {
                    enYakinMesafe = mesafe;
                    enYakinIdx = idx;
                }
            }

            if (enYakinIdx >= 0) {
                sirali.push(enYakinIdx);
                ziyaretEdilmemis.delete(enYakinIdx);
                mevcutIdx = enYakinIdx;
            }
        }

        // Sırayı uygula
        this.siraUygula(sirali);
    }

    /**
     * X veya Y öncelikli sıralama
     */
    private xySirala(xOnce: boolean): void {
        const indeksler = this.delikNoktalar.map((_, i) => i);
        indeksler.sort((a, b) => {
            const na = this.delikNoktalar[a];
            const nb = this.delikNoktalar[b];
            if (xOnce) {
                return na.x !== nb.x ? na.x - nb.x : na.y - nb.y;
            } else {
                return na.y !== nb.y ? na.y - nb.y : na.x - nb.x;
            }
        });
        this.siraUygula(indeksler);
    }

    /**
     * Spiral sıralama (dıştan içe)
     */
    private spiralSirala(): void {
        // Merkez hesapla
        let merkezX = 0, merkezY = 0;
        for (const n of this.delikNoktalar) {
            merkezX += n.x;
            merkezY += n.y;
        }
        merkezX /= this.delikNoktalar.length;
        merkezY /= this.delikNoktalar.length;

        // Açı ve mesafeye göre sırala
        const indeksler = this.delikNoktalar.map((_, i) => i);
        indeksler.sort((a, b) => {
            const na = this.delikNoktalar[a];
            const nb = this.delikNoktalar[b];
            const mesafeA = Math.sqrt(Math.pow(na.x - merkezX, 2) + Math.pow(na.y - merkezY, 2));
            const mesafeB = Math.sqrt(Math.pow(nb.x - merkezX, 2) + Math.pow(nb.y - merkezY, 2));
            if (Math.abs(mesafeA - mesafeB) > 1) {
                return mesafeB - mesafeA; // Dıştan içe
            }
            const aciA = Math.atan2(na.y - merkezY, na.x - merkezX);
            const aciB = Math.atan2(nb.y - merkezY, nb.x - merkezX);
            return aciA - aciB;
        });
        this.siraUygula(indeksler);
    }

    private noktaMesafe(a: FikoVektor3D, b: FikoVektor3D): number {
        return Math.sqrt(Math.pow(a.x - b.x, 2) + Math.pow(a.y - b.y, 2));
    }

    private siraUygula(indeksler: number[]): void {
        const yeniNoktalar = indeksler.map(i => this.delikNoktalar[i]);
        const yeniCaplar = indeksler.map(i => this.delikCaplar[i]);
        const yeniDerinlikler = indeksler.map(i => this.delikDerinlikler[i]);
        this.delikNoktalar = yeniNoktalar;
        this.delikCaplar = yeniCaplar;
        this.delikDerinlikler = yeniDerinlikler;
    }

    // ═══════════════════════════════════════════════════════════════════
    // TAKİM YOLU HESAPLAMA
    // ═══════════════════════════════════════════════════════════════════

    /**
     * Ana takım yolu hesaplama metodu
     */
    takimYoluHesapla(): void {
        this.takimYoluNoktalar = [];

        if (this.delikNoktalar.length === 0) return;

        // Delikleri optimize et
        this.delikleriOptimizeEt();

        // İlk güvenli Z'ye git
        this.takimYoluNoktalar.push(new FikoVektorTakimYol3D(
            this.delikNoktalar[0].x,
            this.delikNoktalar[0].y,
            this.guvenliZ,
            'rapid'
        ));

        // Her delik için işlem
        for (let i = 0; i < this.delikNoktalar.length; i++) {
            const nokta = this.delikNoktalar[i];
            const derinlik = this.delikDerinlikler[i] || Math.abs(this.altZ - this.ustZ);

            // Delik tipine göre işlem
            switch (this.delikTipi) {
                case 'Punta':
                    this.puntaIslemHesapla(nokta, i);
                    break;
                case 'Delme':
                    this.delmeIslemHesapla(nokta, derinlik, i);
                    break;
                case 'Rayba':
                    this.raybaIslemHesapla(nokta, derinlik, i);
                    break;
                case 'Kilavuz':
                    this.kilavuzIslemHesapla(nokta, i);
                    break;
                case 'Havsa':
                    this.havsaIslemHesapla(nokta, i);
                    break;
            }

            // Sonraki deliğe geçiş (son değilse)
            if (i < this.delikNoktalar.length - 1) {
                const sonrakiNokta = this.delikNoktalar[i + 1];
                // Rapid ile sonraki deliğe
                this.takimYoluNoktalar.push(new FikoVektorTakimYol3D(
                    sonrakiNokta.x,
                    sonrakiNokta.y,
                    this.retraktZ,
                    'rapid'
                ));
            }
        }

        // Son güvenli Z'ye çık
        const sonNokta = this.delikNoktalar[this.delikNoktalar.length - 1];
        this.takimYoluNoktalar.push(new FikoVektorTakimYol3D(
            sonNokta.x,
            sonNokta.y,
            this.guvenliZ,
            'rapid'
        ));

        // 3D görüntü güncelle
        this.takimYolu3DGuncelle();
        this.hesaplananSure = this.sureHesapla();
    }

    // ═══════════════════════════════════════════════════════════════════
    // PUNTA İŞLEMİ (G81 - SPOT DRILLING)
    // ═══════════════════════════════════════════════════════════════════

    private puntaIslemHesapla(nokta: FikoVektor3D, idx: number): void {
        // R düzlemine rapid
        this.takimYoluNoktalar.push(new FikoVektorTakimYol3D(
            nokta.x,
            nokta.y,
            this.retraktZ,
            'rapid'
        ));

        // Üst yüzeye yaklaş
        this.takimYoluNoktalar.push(new FikoVektorTakimYol3D(
            nokta.x,
            nokta.y,
            this.ustZ,
            'cut',
            this.puntaIlerleme
        ));

        // Punta derinliğine del
        const hedefZ = this.ustZ - this.puntaDerinlik;
        this.takimYoluNoktalar.push(new FikoVektorTakimYol3D(
            nokta.x,
            nokta.y,
            hedefZ,
            'cut',
            this.puntaIlerleme
        ));

        // Dip bekleme (varsa)
        if (this.dipBekleme > 0) {
            this.takimYoluNoktalar.push(new FikoVektorTakimYol3D(
                nokta.x,
                nokta.y,
                hedefZ,
                'dwell',
                0,
                this.dipBekleme
            ));
        }

        // Retrakt
        this.takimYoluNoktalar.push(new FikoVektorTakimYol3D(
            nokta.x,
            nokta.y,
            this.retraktZ,
            'rapid'
        ));
    }

    // ═══════════════════════════════════════════════════════════════════
    // DELME İŞLEMİ (G83 - PECK DRILLING)
    // ═══════════════════════════════════════════════════════════════════

    private delmeIslemHesapla(nokta: FikoVektor3D, derinlik: number, idx: number): void {
        // R düzlemine rapid
        this.takimYoluNoktalar.push(new FikoVektorTakimYol3D(
            nokta.x,
            nokta.y,
            this.retraktZ,
            'rapid'
        ));

        const hedefZ = this.ustZ - derinlik;
        let mevcutZ = this.ustZ;
        let peckIdx = 0;
        let currentPeckDerinlik = this.ilkPeckDerinlik > 0 ? this.ilkPeckDerinlik : this.peckDerinlik;

        // Peck döngüsü
        while (mevcutZ > hedefZ) {
            // Sonraki peck derinliği
            let sonrakiZ = mevcutZ - currentPeckDerinlik;
            if (sonrakiZ < hedefZ) sonrakiZ = hedefZ;

            if (peckIdx > 0) {
                // Önceki deliğe hızlı yaklaşma (chip clear offset)
                const hizliYaklasmaZ = mevcutZ + this.peckRetraktMiktari;
                this.takimYoluNoktalar.push(new FikoVektorTakimYol3D(
                    nokta.x,
                    nokta.y,
                    hizliYaklasmaZ,
                    'rapid'
                ));
            }

            // Peck delme
            this.takimYoluNoktalar.push(new FikoVektorTakimYol3D(
                nokta.x,
                nokta.y,
                sonrakiZ,
                'cut',
                this.ilerlemeDelme
            ));

            // Peck bekleme
            if (this.peckBekleme > 0) {
                this.takimYoluNoktalar.push(new FikoVektorTakimYol3D(
                    nokta.x,
                    nokta.y,
                    sonrakiZ,
                    'dwell',
                    0,
                    this.peckBekleme
                ));
            }

            mevcutZ = sonrakiZ;
            peckIdx++;

            // Henüz hedefe ulaşmadıysak retrakt
            if (mevcutZ > hedefZ) {
                const retraktHedef = this.peckTipi === 'TamRetrakt'
                    ? this.guvenliZ
                    : this.retraktZ;

                this.takimYoluNoktalar.push(new FikoVektorTakimYol3D(
                    nokta.x,
                    nokta.y,
                    retraktHedef,
                    'rapid'
                ));

                // Peck derinliğini azalt (varsa)
                if (this.peckAzaltmaOrani > 0) {
                    currentPeckDerinlik *= (1 - this.peckAzaltmaOrani / 100);
                    if (currentPeckDerinlik < this.minPeckDerinlik) {
                        currentPeckDerinlik = this.minPeckDerinlik;
                    }
                }
            }
        }

        // Dip bekleme (varsa)
        if (this.dipBekleme > 0) {
            this.takimYoluNoktalar.push(new FikoVektorTakimYol3D(
                nokta.x,
                nokta.y,
                hedefZ,
                'dwell',
                0,
                this.dipBekleme
            ));
        }

        // Final retrakt
        this.takimYoluNoktalar.push(new FikoVektorTakimYol3D(
            nokta.x,
            nokta.y,
            this.retraktZ,
            'rapid'
        ));
    }

    // ═══════════════════════════════════════════════════════════════════
    // RAYBA İŞLEMİ (G85 - REAMING)
    // ═══════════════════════════════════════════════════════════════════

    private raybaIslemHesapla(nokta: FikoVektor3D, derinlik: number, idx: number): void {
        // R düzlemine rapid
        this.takimYoluNoktalar.push(new FikoVektorTakimYol3D(
            nokta.x,
            nokta.y,
            this.retraktZ,
            'rapid'
        ));

        const hedefZ = this.ustZ - derinlik;

        // Üst yüzeye yaklaş
        this.takimYoluNoktalar.push(new FikoVektorTakimYol3D(
            nokta.x,
            nokta.y,
            this.ustZ,
            'cut',
            this.raybaIlerleme
        ));

        // Rayba derinliğine in (düşük ilerleme)
        this.takimYoluNoktalar.push(new FikoVektorTakimYol3D(
            nokta.x,
            nokta.y,
            hedefZ,
            'cut',
            this.raybaIlerleme
        ));

        // Dip bekleme (yüzey kalitesi için)
        if (this.raybaDwellBottom > 0) {
            this.takimYoluNoktalar.push(new FikoVektorTakimYol3D(
                nokta.x,
                nokta.y,
                hedefZ,
                'dwell',
                0,
                this.raybaDwellBottom
            ));
        }

        // Feed ile geri çık (G85 özelliği - rapid değil!)
        this.takimYoluNoktalar.push(new FikoVektorTakimYol3D(
            nokta.x,
            nokta.y,
            this.retraktZ,
            'cut',
            this.raybaCikisHizi
        ));
    }

    // ═══════════════════════════════════════════════════════════════════
    // KILAVUZ İŞLEMİ (G84 - TAPPING)
    // ═══════════════════════════════════════════════════════════════════

    private kilavuzIslemHesapla(nokta: FikoVektor3D, idx: number): void {
        // R düzlemine rapid
        this.takimYoluNoktalar.push(new FikoVektorTakimYol3D(
            nokta.x,
            nokta.y,
            this.retraktZ,
            'rapid'
        ));

        const hedefZ = this.ustZ - this.disDerinlik;

        // Kılavuz ilerleme hesapla (pitch * devir)
        const kilavuzIlerleme = this.disAdimi * this.kilavuzDevir;

        // Diş çekme (senkronize hareket)
        const tapPoint = new FikoVektorTakimYol3D(
            nokta.x,
            nokta.y,
            hedefZ,
            'tap',
            kilavuzIlerleme,
            0,
            this.disAdimi
        );
        tapPoint.arcCW = this.disYonu !== 'Sol'; // CW = sağ diş
        this.takimYoluNoktalar.push(tapPoint);

        // Geri çekme (spindle reverse)
        const retractPoint = new FikoVektorTakimYol3D(
            nokta.x,
            nokta.y,
            this.retraktZ,
            'tap_retract',
            kilavuzIlerleme,
            0,
            this.disAdimi
        );
        retractPoint.arcCW = this.disYonu === 'Sol'; // Geri çekme ters yön
        this.takimYoluNoktalar.push(retractPoint);
    }

    // ═══════════════════════════════════════════════════════════════════
    // HAVŞA İŞLEMİ (G82 - COUNTERBORE)
    // ═══════════════════════════════════════════════════════════════════

    private havsaIslemHesapla(nokta: FikoVektor3D, idx: number): void {
        // R düzlemine rapid
        this.takimYoluNoktalar.push(new FikoVektorTakimYol3D(
            nokta.x,
            nokta.y,
            this.retraktZ,
            'rapid'
        ));

        const hedefZ = this.ustZ - this.havsaDerinlik;

        // Üst yüzeye yaklaş
        this.takimYoluNoktalar.push(new FikoVektorTakimYol3D(
            nokta.x,
            nokta.y,
            this.ustZ,
            'cut',
            this.ilerlemeDelme
        ));

        // Havşa derinliğine in
        this.takimYoluNoktalar.push(new FikoVektorTakimYol3D(
            nokta.x,
            nokta.y,
            hedefZ,
            'cut',
            this.ilerlemeDelme
        ));

        // Dip bekleme (yüzey kalitesi için - G82 özelliği)
        if (this.havsaDwell > 0) {
            this.takimYoluNoktalar.push(new FikoVektorTakimYol3D(
                nokta.x,
                nokta.y,
                hedefZ,
                'dwell',
                0,
                this.havsaDwell
            ));
        }

        // Retrakt
        this.takimYoluNoktalar.push(new FikoVektorTakimYol3D(
            nokta.x,
            nokta.y,
            this.retraktZ,
            'rapid'
        ));
    }

    // ═══════════════════════════════════════════════════════════════════
    // 3D GÖRÜNTÜLEME
    // ═══════════════════════════════════════════════════════════════════

    private delikMarkerOlustur(nokta: FikoVektor3D): void {
        // Delik merkezi marker
        const geometry = new THREE.RingGeometry(
            this.takim.cap / 2 - 0.5,
            this.takim.cap / 2,
            32
        );
        const material = new THREE.MeshBasicMaterial({
            color: this.delikTipiRenk(),
            side: THREE.DoubleSide,
            transparent: true,
            opacity: 0.7
        });
        const ring = new THREE.Mesh(geometry, material);
        ring.position.set(nokta.x, nokta.y, nokta.z + 0.1);
        ring.rotation.x = -Math.PI / 2;
        this.delikMarkerlar.add(ring);

        // Merkez noktası
        const dotGeom = new THREE.CircleGeometry(0.5, 16);
        const dotMat = new THREE.MeshBasicMaterial({
            color: 0xffffff,
            side: THREE.DoubleSide
        });
        const dot = new THREE.Mesh(dotGeom, dotMat);
        dot.position.set(nokta.x, nokta.y, nokta.z + 0.15);
        dot.rotation.x = -Math.PI / 2;
        this.delikMarkerlar.add(dot);
    }

    private delikTipiRenk(): number {
        switch (this.delikTipi) {
            case 'Punta': return 0x00ff00;      // Yeşil
            case 'Delme': return 0x0088ff;      // Mavi
            case 'Rayba': return 0xff8800;      // Turuncu
            case 'Kilavuz': return 0xff0088;    // Pembe
            case 'Havsa': return 0x8800ff;      // Mor
            default: return 0xffffff;
        }
    }

    private takimYolu3DGuncelle(): void {
        // Eski mesh'leri temizle
        while (this.takimYoluMesh.children.length > 0) {
            const child = this.takimYoluMesh.children[0];
            this.takimYoluMesh.remove(child);
            if ((child as any).geometry) (child as any).geometry.dispose();
            if ((child as any).material) (child as any).material.dispose();
        }

        if (this.takimYoluNoktalar.length < 2) return;

        // Rapid hareketler (sarı kesikli)
        const rapidPoints: THREE.Vector3[] = [];
        // Kesim hareketleri (mavi)
        const cutPoints: THREE.Vector3[] = [];

        for (let i = 0; i < this.takimYoluNoktalar.length - 1; i++) {
            const p1 = this.takimYoluNoktalar[i];
            const p2 = this.takimYoluNoktalar[i + 1];

            if (p2.tip === 'rapid') {
                rapidPoints.push(new THREE.Vector3(p1.x, p1.y, p1.z));
                rapidPoints.push(new THREE.Vector3(p2.x, p2.y, p2.z));
            } else {
                cutPoints.push(new THREE.Vector3(p1.x, p1.y, p1.z));
                cutPoints.push(new THREE.Vector3(p2.x, p2.y, p2.z));
            }
        }

        // Rapid çizgiler
        if (rapidPoints.length >= 2) {
            const rapidGeom = new THREE.BufferGeometry().setFromPoints(rapidPoints);
            const rapidMat = new THREE.LineDashedMaterial({
                color: 0xffff00,
                dashSize: 2,
                gapSize: 1,
                linewidth: 1
            });
            const rapidLine = new THREE.LineSegments(rapidGeom, rapidMat);
            rapidLine.computeLineDistances();
            this.takimYoluMesh.add(rapidLine);
        }

        // Kesim çizgiler
        if (cutPoints.length >= 2) {
            const cutGeom = new THREE.BufferGeometry().setFromPoints(cutPoints);
            const cutMat = new THREE.LineBasicMaterial({
                color: this.delikTipiRenk(),
                linewidth: 2
            });
            const cutLine = new THREE.LineSegments(cutGeom, cutMat);
            this.takimYoluMesh.add(cutLine);
        }
    }

    // ═══════════════════════════════════════════════════════════════════
    // G-CODE ÇIKTISI
    // ═══════════════════════════════════════════════════════════════════

    /**
     * G-Code üret
     */
    gCodeUret(): string {
        let gcode = '';

        // Header
        gcode += '%\n';
        gcode += `O${1000 + FikoAktiviteDelik.SAYAC} (${this.name})\n`;
        gcode += '(FikoYT CAM - Delik Isleme)\n';
        gcode += `(Delik Tipi: ${this.delikTipi})\n`;
        gcode += `(Takim: ${this.takim.ad} D=${this.takim.cap}mm)\n`;
        gcode += `(Delik Sayisi: ${this.delikNoktalar.length})\n`;
        gcode += '\n';

        // Ayarlar
        gcode += 'G21 (Metric)\n';
        gcode += 'G90 (Absolute)\n';
        gcode += `G54 (Work Offset)\n`;
        gcode += '\n';

        // Spindle
        gcode += `S${this.devir} M03 (Spindle CW)\n`;

        // Soğutma
        if (this.sogutmaTipi !== 'Yok') {
            gcode += this.sogutmaTipi === 'Ic' ? 'M08 (Coolant Through)\n' : 'M08 (Coolant On)\n';
        }
        gcode += '\n';

        // Güvenli Z
        gcode += `G00 Z${this.guvenliZ.toFixed(3)} (Safe Z)\n`;

        // Delik döngüsü seç
        const dongu = this.gCodeDonguSec();
        const donguParams = this.gCodeDonguParams();

        // İlk pozisyona git
        if (this.delikNoktalar.length > 0) {
            const ilk = this.delikNoktalar[0];
            gcode += `G00 X${ilk.x.toFixed(3)} Y${ilk.y.toFixed(3)}\n`;
        }

        // Canned cycle başlat
        gcode += `${dongu} ${donguParams}\n`;

        // Delik noktaları
        for (let i = 1; i < this.delikNoktalar.length; i++) {
            const nokta = this.delikNoktalar[i];
            gcode += `X${nokta.x.toFixed(3)} Y${nokta.y.toFixed(3)}\n`;
        }

        // Döngü iptal
        gcode += 'G80 (Cancel Cycle)\n';
        gcode += '\n';

        // Bitiş
        gcode += `G00 Z${this.guvenliZ.toFixed(3)}\n`;
        if (this.sogutmaTipi !== 'Yok') gcode += 'M09 (Coolant Off)\n';
        gcode += 'M05 (Spindle Stop)\n';
        gcode += 'G28 G91 Z0 (Return Home Z)\n';
        gcode += 'G28 X0 Y0 (Return Home XY)\n';
        gcode += 'M30 (Program End)\n';
        gcode += '%\n';

        return gcode;
    }

    private gCodeDonguSec(): string {
        switch (this.delikTipi) {
            case 'Punta':
                return this.dipBekleme > 0 ? 'G82' : 'G81';
            case 'Delme':
                return this.peckTipi === 'YuksekHiz' ? 'G73' : 'G83';
            case 'Rayba':
                return 'G85';
            case 'Kilavuz':
                return this.rigidTapping ? 'G84' : 'G84';
            case 'Havsa':
                return 'G82';
            default:
                return 'G81';
        }
    }

    private gCodeDonguParams(): string {
        let params = '';
        const hedefZ = this.ustZ - this.getAktifDerinlik();

        switch (this.delikTipi) {
            case 'Punta':
                params = `Z${hedefZ.toFixed(3)} R${this.retraktZ.toFixed(3)} F${this.puntaIlerleme}`;
                if (this.dipBekleme > 0) params += ` P${Math.round(this.dipBekleme * 1000)}`;
                break;

            case 'Delme':
                params = `Z${hedefZ.toFixed(3)} R${this.retraktZ.toFixed(3)} Q${this.peckDerinlik.toFixed(3)} F${this.ilerlemeDelme}`;
                break;

            case 'Rayba':
                params = `Z${hedefZ.toFixed(3)} R${this.retraktZ.toFixed(3)} F${this.raybaIlerleme}`;
                if (this.raybaDwellBottom > 0) params += ` P${Math.round(this.raybaDwellBottom * 1000)}`;
                break;

            case 'Kilavuz':
                const kilavuzF = this.disAdimi * this.kilavuzDevir;
                params = `Z${hedefZ.toFixed(3)} R${this.retraktZ.toFixed(3)} F${kilavuzF.toFixed(1)}`;
                if (this.disYonu === 'Sol') params = 'M04\n' + params; // CCW için
                break;

            case 'Havsa':
                params = `Z${hedefZ.toFixed(3)} R${this.retraktZ.toFixed(3)} P${Math.round(this.havsaDwell * 1000)} F${this.ilerlemeDelme}`;
                break;
        }

        return params;
    }

    private getAktifDerinlik(): number {
        switch (this.delikTipi) {
            case 'Punta': return this.puntaDerinlik;
            case 'Delme': return Math.abs(this.altZ - this.ustZ);
            case 'Rayba': return Math.abs(this.altZ - this.ustZ);
            case 'Kilavuz': return this.disDerinlik;
            case 'Havsa': return this.havsaDerinlik;
            default: return Math.abs(this.altZ - this.ustZ);
        }
    }

    // ═══════════════════════════════════════════════════════════════════
    // SÜRE HESAPLAMA
    // ═══════════════════════════════════════════════════════════════════

    private sureHesapla(): number {
        let toplam = 0;

        for (let i = 0; i < this.takimYoluNoktalar.length - 1; i++) {
            const p1 = this.takimYoluNoktalar[i];
            const p2 = this.takimYoluNoktalar[i + 1];
            const mesafe = Math.sqrt(
                Math.pow(p2.x - p1.x, 2) +
                Math.pow(p2.y - p1.y, 2) +
                Math.pow(p2.z - p1.z, 2)
            );

            const hiz = p2.tip === 'rapid' ? this.hizRapid : (p2.ilerleme || this.ilerlemeDelme);
            toplam += mesafe / hiz;

            // Dwell süreleri
            if (p2.tip === 'dwell' && p2.dwell) {
                toplam += p2.dwell / 60;
            }
        }

        return toplam;
    }

    // ═══════════════════════════════════════════════════════════════════
    // JSON SERİLEŞTİRME
    // ═══════════════════════════════════════════════════════════════════

    toJSON(): any {
        return {
            type: this.type,
            ad: this.name,
            delikTipi: this.delikTipi,
            delikNoktalar: this.delikNoktalar.map(n => ({ x: n.x, y: n.y, z: n.z })),
            delikCaplar: this.delikCaplar,
            delikDerinlikler: this.delikDerinlikler,
            ustZ: this.ustZ,
            altZ: this.altZ,
            guvenliZ: this.guvenliZ,
            retraktZ: this.retraktZ,

            // Punta
            puntaDerinlik: this.puntaDerinlik,
            puntaAci: this.puntaAci,

            // Delme
            peckDerinlik: this.peckDerinlik,
            peckRetraktMiktari: this.peckRetraktMiktari,
            peckBekleme: this.peckBekleme,
            peckTipi: this.peckTipi,
            ilkPeckDerinlik: this.ilkPeckDerinlik,
            peckAzaltmaOrani: this.peckAzaltmaOrani,
            minPeckDerinlik: this.minPeckDerinlik,

            // Rayba
            raybaStok: this.raybaStok,
            raybaDwellBottom: this.raybaDwellBottom,
            raybaCikisHizi: this.raybaCikisHizi,

            // Kılavuz
            disCap: this.disCap,
            disAdimi: this.disAdimi,
            disTipi: this.disTipi,
            disDerinlik: this.disDerinlik,
            disYonu: this.disYonu,
            rigidTapping: this.rigidTapping,
            floatingHolder: this.floatingHolder,
            kilavuzBoyOrani: this.kilavuzBoyOrani,

            // Havşa
            havsaTipi: this.havsaTipi,
            havsaCap: this.havsaCap,
            havsaDerinlik: this.havsaDerinlik,
            havsaAci: this.havsaAci,
            havsaDwell: this.havsaDwell,

            // Hız
            ilerlemeDelme: this.ilerlemeDelme,
            ilerlemeRetrakt: this.ilerlemeRetrakt,
            hizRapid: this.hizRapid,
            devir: this.devir,
            puntaIlerleme: this.puntaIlerleme,
            raybaIlerleme: this.raybaIlerleme,
            kilavuzDevir: this.kilavuzDevir,

            // Optimizasyon
            siralama: this.siralama,
            onPuntaEtkin: this.onPuntaEtkin,
            onPuntaDerinlik: this.onPuntaDerinlik,
            dipBekleme: this.dipBekleme,
            chipBreakEtkin: this.chipBreakEtkin,

            // Güvenlik
            delikDerinlikKontrol: this.delikDerinlikKontrol,
            maxDerinlikOrani: this.maxDerinlikOrani,

            // Soğutma
            sogutmaTipi: this.sogutmaTipi,
            sogutmaBasinc: this.sogutmaBasinc,

            // Takım
            takim: this.takim.toJSON ? this.takim.toJSON() : {
                ad: this.takim.ad,
                cap: this.takim.cap,
                takimTipi: this.takim.takimTipi
            }
        };
    }

    static fromJSON(json: any, sahne?: FikoSahne): FikoAktiviteDelik {
        const akt = new FikoAktiviteDelik(sahne);

        akt.name = json.ad || akt.name;
        akt.delikTipi = json.delikTipi || 'Delme';
        akt.delikNoktalar = (json.delikNoktalar || []).map((n: any) => new FikoVektor3D(n.x, n.y, n.z));
        akt.delikCaplar = json.delikCaplar || [];
        akt.delikDerinlikler = json.delikDerinlikler || [];
        akt.ustZ = json.ustZ ?? 0;
        akt.altZ = json.altZ ?? -10;
        akt.guvenliZ = json.guvenliZ ?? 5;
        akt.retraktZ = json.retraktZ ?? 2;

        // Punta
        akt.puntaDerinlik = json.puntaDerinlik ?? 3;
        akt.puntaAci = json.puntaAci ?? 90;

        // Delme
        akt.peckDerinlik = json.peckDerinlik ?? 5;
        akt.peckRetraktMiktari = json.peckRetraktMiktari ?? 0.5;
        akt.peckBekleme = json.peckBekleme ?? 0;
        akt.peckTipi = json.peckTipi || 'Standart';
        akt.ilkPeckDerinlik = json.ilkPeckDerinlik ?? 0;
        akt.peckAzaltmaOrani = json.peckAzaltmaOrani ?? 0;
        akt.minPeckDerinlik = json.minPeckDerinlik ?? 1;

        // Rayba
        akt.raybaStok = json.raybaStok ?? 0.2;
        akt.raybaDwellBottom = json.raybaDwellBottom ?? 0.5;
        akt.raybaCikisHizi = json.raybaCikisHizi ?? 500;

        // Kılavuz
        akt.disCap = json.disCap ?? 6;
        akt.disAdimi = json.disAdimi ?? 1;
        akt.disTipi = json.disTipi || 'Metrik';
        akt.disDerinlik = json.disDerinlik ?? 10;
        akt.disYonu = json.disYonu || 'Sag';
        akt.rigidTapping = json.rigidTapping ?? false;
        akt.floatingHolder = json.floatingHolder ?? true;
        akt.kilavuzBoyOrani = json.kilavuzBoyOrani ?? 2;

        // Havşa
        akt.havsaTipi = json.havsaTipi || 'Counterbore';
        akt.havsaCap = json.havsaCap ?? 10;
        akt.havsaDerinlik = json.havsaDerinlik ?? 3;
        akt.havsaAci = json.havsaAci ?? 90;
        akt.havsaDwell = json.havsaDwell ?? 0.5;

        // Hız
        akt.ilerlemeDelme = json.ilerlemeDelme ?? 200;
        akt.ilerlemeRetrakt = json.ilerlemeRetrakt ?? 1000;
        akt.hizRapid = json.hizRapid ?? 10000;
        akt.devir = json.devir ?? 3000;
        akt.puntaIlerleme = json.puntaIlerleme ?? 300;
        akt.raybaIlerleme = json.raybaIlerleme ?? 150;
        akt.kilavuzDevir = json.kilavuzDevir ?? 500;

        // Optimizasyon
        akt.siralama = json.siralama || 'EnKisaYol';
        akt.onPuntaEtkin = json.onPuntaEtkin ?? false;
        akt.onPuntaDerinlik = json.onPuntaDerinlik ?? 2;
        akt.dipBekleme = json.dipBekleme ?? 0;
        akt.chipBreakEtkin = json.chipBreakEtkin ?? true;

        // Güvenlik
        akt.delikDerinlikKontrol = json.delikDerinlikKontrol ?? true;
        akt.maxDerinlikOrani = json.maxDerinlikOrani ?? 10;

        // Soğutma
        akt.sogutmaTipi = json.sogutmaTipi || 'Dis';
        akt.sogutmaBasinc = json.sogutmaBasinc || 'Orta';

        // Takım
        if (json.takim) {
            akt.takim.ad = json.takim.ad || 'Matkap';
            akt.takim.cap = json.takim.cap ?? 6;
            akt.takim.takimTipi = json.takim.takimTipi || 'Matkap';
        }

        return akt;
    }

    // ═══════════════════════════════════════════════════════════════════
    // TEMİZLEME
    // ═══════════════════════════════════════════════════════════════════

    temizle(): void {
        this.delikNoktalar = [];
        this.delikCaplar = [];
        this.delikDerinlikler = [];
        this.takimYoluNoktalar = [];

        // Marker'ları temizle
        while (this.delikMarkerlar.children.length > 0) {
            const child = this.delikMarkerlar.children[0];
            this.delikMarkerlar.remove(child);
            if ((child as any).geometry) (child as any).geometry.dispose();
            if ((child as any).material) (child as any).material.dispose();
        }

        // Takım yolunu temizle
        while (this.takimYoluMesh.children.length > 0) {
            const child = this.takimYoluMesh.children[0];
            this.takimYoluMesh.remove(child);
            if ((child as any).geometry) (child as any).geometry.dispose();
            if ((child as any).material) (child as any).material.dispose();
        }
    }

    dispose(): void {
        this.temizle();
        if (typeof (this as any).parent?.remove === 'function') {
            (this as any).parent.remove(this);
        }
    }
}
